// DocProse.tsx — a generated page, with its file references made clickable.
//
// The pages are full of paths: a description names the files it was assembled
// from, and a file summary names what it imports. They are written in backticks
// by the prompts, so they arrive as inline code spans — readable, and dead.
//
// Resolution is done against the tree the page already loaded rather than by
// pattern-matching what looks like a path. That is the whole trick: the client
// knows exactly which paths exist in this repository, so a span becomes a link
// only when it names one. No regex deciding whether `context.Context` is a
// file, no link to something that was renamed three commits ago — a span either
// matches a real node or stays code.
//
// Three ways a span can match, narrowest first: the exact repo-relative path, a
// path relative to the directory being read, and a bare basename that is unique
// in the repository. The last is what makes `main.go` work inside a directory
// page without the author having written the full path; it is deliberately
// refused when the name is ambiguous, because linking `client.go` to whichever
// of nine came first is worse than not linking it.
import { useEffect, useRef } from 'react';
import { Markdown } from 'common';
import type { DocNode } from '../api';

// PathIndex is the lookup a page resolves its code spans through.
export interface PathIndex {
  // exact repo-relative path -> node
  byPath: Map<string, DocNode>;
  // basename -> node, only where that basename is unique in the repository
  byUniqueName: Map<string, DocNode>;
}

// buildPathIndex walks the tree once. Memoised by the caller: a repository of
// two thousand nodes should not be re-indexed on every keystroke elsewhere.
export function buildPathIndex(root: DocNode | null): PathIndex {
  const byPath = new Map<string, DocNode>();
  const seen = new Map<string, DocNode | null>();

  const walk = (node: DocNode) => {
    if (node.path) {
      byPath.set(node.path, node);
      const base = node.name;
      // null marks "ambiguous" — a second sighting poisons the entry rather
      // than overwriting it, so a later duplicate cannot win by arriving last.
      seen.set(base, seen.has(base) ? null : node);
    }
    for (const child of node.children ?? []) walk(child);
  };
  if (root) walk(root);

  const byUniqueName = new Map<string, DocNode>();
  for (const [name, node] of seen) {
    if (node) byUniqueName.set(name, node);
  }
  return { byPath, byUniqueName };
}

// resolvePath finds the node a code span names, or null.
function resolvePath(index: PathIndex, raw: string, fromDir: string): DocNode | null {
  const text = raw.trim().replace(/^\.\//, '').replace(/\/+$/, '');
  // A span with whitespace or a backtick is prose, not a path; one with a
  // paren is a function call written in code font.
  if (!text || /[\s`()]/.test(text)) return null;

  const exact = index.byPath.get(text);
  if (exact) return exact;

  if (fromDir) {
    const joined = index.byPath.get(`${fromDir}/${text}`);
    if (joined) return joined;
  }
  // Only when the name is unambiguous repository-wide.
  if (!text.includes('/')) {
    const unique = index.byUniqueName.get(text);
    if (unique) return unique;
  }
  return null;
}

export default function DocProse({ content, index, fromDir, onSelect, className }: {
  content: string;
  index: PathIndex;
  // The directory the page describes, so a bare name resolves beside it first.
  fromDir: string;
  onSelect: (node: DocNode) => void;
  className?: string;
}) {
  const host = useRef<HTMLDivElement | null>(null);

  // The shared Markdown component renders its own elements and takes no
  // component overrides, and it is used by every product in the estate — so
  // the links are attached here instead of widening that surface for one
  // page's benefit.
  //
  // Two passes over the rendered output: mark the spans that resolve, then let
  // one delegated listener handle every click. Delegation rather than a
  // listener per span because a directory page can carry a hundred of them,
  // and marking rather than styling-on-hover because a reader should be able
  // to see which names are live without hunting for them with the pointer.
  useEffect(() => {
    const root = host.current;
    if (!root) return;
    for (const el of Array.from(root.querySelectorAll('code'))) {
      // Inline only: a fenced block is a code sample, and a path inside one is
      // being shown, not referenced.
      if (el.closest('pre')) continue;
      const node = resolvePath(index, el.textContent ?? '', fromDir);
      if (!node) {
        el.classList.remove('doc-link');
        el.removeAttribute('title');
        continue;
      }
      el.classList.add('doc-link');
      el.setAttribute('title', node.has_doc
        ? `Open ${node.path}`
        : `${node.path} — walked, but no summary generated yet`);
    }
  }, [content, index, fromDir]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = (e.target as HTMLElement).closest('code');
    if (!el || el.closest('pre')) return;
    const node = resolvePath(index, el.textContent ?? '', fromDir);
    if (!node) return;
    e.preventDefault();
    onSelect(node);
  };

  return (
    <div ref={host} onClick={handleClick}>
      <Markdown className={className}>{content}</Markdown>
    </div>
  );
}
